import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { useMemo, useState } from "react";

type Option = {
  value: string;
  label: string;
};

type BaseProps = {
  options: Option[];
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  /** Label shown when search returns no results (default: "No results") */
  noResultsLabel?: string;
  /** Template for multi-select count, use {count} placeholder (default: "{count} selected") */
  selectedCountLabel?: string;
};

type SingleProps = BaseProps & {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiProps = BaseProps & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type ComboboxProps = SingleProps | MultiProps;

function ChevronIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-[color:var(--text-muted)]"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SingleCombobox({
  options,
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  className,
  noResultsLabel = "No results",
  "aria-label": ariaLabel,
}: SingleProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query) return options;
    const lower = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, query]);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  return (
    <HeadlessCombobox
      value={selectedOption}
      onChange={(opt: Option | null) => onChange(opt?.value ?? null)}
      disabled={disabled}
      immediate
    >
      <div className={`relative w-full ${className ?? ""}`}>
        <div
          className={`flex h-11 w-full items-center rounded-md border lg:h-[35px] bg-[color:var(--surface)] transition ${
            hasError
              ? "border-[color:var(--error-500)]"
              : "border-[color:var(--border-light)] focus-within:border-[color:var(--primary-500)]"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <ComboboxInput
            className="h-full w-full bg-transparent px-3 text-[16px] focus:outline-none disabled:cursor-not-allowed lg:text-[13px]"
            displayValue={(opt: Option | null) => opt?.label ?? ""}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-label={ariaLabel}
          />
          <ComboboxButton className="flex h-full items-center px-3">
            <ChevronIcon />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-md)] empty:hidden"
          anchor={false}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {noResultsLabel}
            </div>
          ) : (
            filtered.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)] data-[selected]:font-medium"
              >
                {({ selected }) => (
                  <>
                    <span className="flex-1 truncate">{option.label}</span>
                    {selected && (
                      <span className="text-[color:var(--primary-500)]">
                        <CheckIcon />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}

function MultiCombobox({
  options,
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  className,
  noResultsLabel = "No results",
  selectedCountLabel = "{count} selected",
  "aria-label": ariaLabel,
}: MultiProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query) return options;
    const lower = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, query]);

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value]
  );

  const summaryText = useMemo(() => {
    if (selectedOptions.length === 0) return "";
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return selectedCountLabel.replace("{count}", String(selectedOptions.length));
  }, [selectedOptions, selectedCountLabel]);

  return (
    <HeadlessCombobox
      value={selectedOptions}
      onChange={(opts: Option[]) => onChange(opts.map((o) => o.value))}
      multiple
      disabled={disabled}
      by="value"
      immediate
    >
      <div className={`relative w-full ${className ?? ""}`}>
        <div
          className={`flex h-11 w-full items-center rounded-md border lg:h-[35px] bg-[color:var(--surface)] transition ${
            hasError
              ? "border-[color:var(--error-500)]"
              : "border-[color:var(--border-light)] focus-within:border-[color:var(--primary-500)]"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <ComboboxInput
            className="h-full w-full bg-transparent px-3 text-[16px] focus:outline-none disabled:cursor-not-allowed lg:text-[13px]"
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            placeholder={selectedOptions.length === 0 ? placeholder : summaryText}
            aria-label={ariaLabel}
          />
          <ComboboxButton className="flex h-full items-center px-3">
            <ChevronIcon />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[var(--shadow-md)] empty:hidden"
          anchor={false}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[color:var(--text-muted)]">
              {noResultsLabel}
            </div>
          ) : (
            filtered.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--background-muted)]"
              >
                {({ selected }) => (
                  <>
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      className="h-4 w-4 rounded border-[color:var(--border-light)] accent-[color:var(--primary-500)]"
                      tabIndex={-1}
                    />
                    <span className="flex-1 truncate">{option.label}</span>
                  </>
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}

export function Combobox(props: ComboboxProps) {
  if (props.multiple) {
    return <MultiCombobox {...props} />;
  }
  return <SingleCombobox {...props} />;
}

export type { ComboboxProps, Option as ComboboxOption };
